import * as LabelPrimitive from '@radix-ui/react-label';
import { AlertCircle } from 'lucide-react';
import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

import { cn } from '@/lib/utils';

/*
 * A labelled input, and the label is not optional.
 *
 * design-system.md §8: every input needs a `name` and a bound `<label for>`.
 * The prototype's careers form has neither, so password managers and autofill
 * cannot see it and screen readers have to guess from placement. Binding them
 * here rather than at each call site means a field cannot be built without one.
 *
 * The error is wired with `aria-describedby` and `aria-invalid`, so it is
 * announced rather than only coloured — colour alone is not an error message.
 */

export function Label({
  className,
  required,
  children,
  ...props
}: LabelPrimitive.LabelProps & { required?: boolean }) {
  return (
    <LabelPrimitive.Root className={cn('block text-label text-ink-900', className)} {...props}>
      {children}
      {required ? (
        <>
          <span aria-hidden="true" className="ml-0.5 text-bark-700">
            *
          </span>
          <span className="sr-only"> (required)</span>
        </>
      ) : null}
    </LabelPrimitive.Root>
  );
}

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /** Sits under the input, before any error. Say what to type, not what went wrong. */
  hint?: ReactNode;
  error?: string | undefined;
}

export function Field({ label, hint, error, required, className, name, ...props }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} required={required}>
        {label}
      </Label>

      <input
        id={id}
        // Without a name, a password manager cannot offer to fill or save this.
        name={name ?? label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={cn(hint ? hintId : undefined, error ? errorId : undefined) || undefined}
        className={cn(
          'min-h-control w-full rounded-sm border bg-white px-3 text-body',
          'placeholder:text-ink-500',
          error ? 'border-status-priority-text' : 'border-ink-300',
          className,
        )}
        {...props}
      />

      {hint ? (
        <p id={hintId} className="text-meta text-ink-500">
          {hint}
        </p>
      ) : null}

      {error ? (
        // `role="alert"` so a validation failure is announced when it appears,
        // rather than being a colour a screen reader never mentions.
        <p
          id={errorId}
          role="alert"
          className="flex items-start gap-1.5 text-meta text-status-priority-text"
        >
          <AlertCircle aria-hidden="true" className="mt-px size-3.5 shrink-0" />
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface SelectFieldProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'id' | 'children'
> {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  /** A blank first option — "please choose". Omit where a default is correct. */
  placeholder?: string;
  options: readonly { value: string; label: string }[];
}

/** A `<select>` with the same label and error wiring as `Field`. */
export function SelectField({
  label,
  hint,
  error,
  required,
  placeholder,
  options,
  className,
  name,
  ...props
}: SelectFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} required={required}>
        {label}
      </Label>

      <select
        id={id}
        name={name ?? label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={cn(hint ? hintId : undefined, error ? errorId : undefined) || undefined}
        className={cn(
          'min-h-control w-full rounded-sm border bg-white px-3 text-body',
          error ? 'border-status-priority-text' : 'border-ink-300',
          className,
        )}
        {...props}
      >
        {placeholder === undefined ? null : <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {hint ? (
        <p id={hintId} className="text-meta text-ink-500">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p
          id={errorId}
          role="alert"
          className="flex items-start gap-1.5 text-meta text-status-priority-text"
        >
          <AlertCircle aria-hidden="true" className="mt-px size-3.5 shrink-0" />
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface TextareaFieldProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'id'
> {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
}

/** A `<textarea>` with the same wiring. */
export function TextareaField({
  label,
  hint,
  error,
  required,
  className,
  name,
  rows = 4,
  ...props
}: TextareaFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} required={required}>
        {label}
      </Label>

      <textarea
        id={id}
        name={name ?? label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
        required={required}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={cn(hint ? hintId : undefined, error ? errorId : undefined) || undefined}
        className={cn(
          'w-full rounded-sm border bg-white px-3 py-2.5 text-body',
          'placeholder:text-ink-500',
          error ? 'border-status-priority-text' : 'border-ink-300',
          className,
        )}
        {...props}
      />

      {hint ? (
        <p id={hintId} className="text-meta text-ink-500">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p
          id={errorId}
          role="alert"
          className="flex items-start gap-1.5 text-meta text-status-priority-text"
        >
          <AlertCircle aria-hidden="true" className="mt-px size-3.5 shrink-0" />
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A checkbox with its label bound and its hit area at 44px.
 *
 * **The hit area is the label**, not the 16px box. design-system.md §9.6 asks
 * for 44px on everything a finger has to reach and says to keep the visual size
 * where it must be small — a checkbox is the clearest case of that.
 */
export function CheckboxField({
  label,
  name,
  checked,
  onCheckedChange,
  ...props
}: {
  label: ReactNode;
  name: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'checked' | 'onChange' | 'name'>) {
  const id = useId();

  return (
    <div className="flex min-h-touch items-center">
      <input
        id={id}
        name={name}
        type="checkbox"
        checked={checked}
        onChange={(event) => {
          onCheckedChange(event.target.checked);
        }}
        className="size-[18px] shrink-0 rounded-sm border-ink-300 accent-honey-500"
        {...props}
      />
      {/* `py-3` so the label itself is a 44px target, not just the box. */}
      <label htmlFor={id} className="cursor-pointer py-3 pl-2.5 text-body text-ink-900">
        {label}
      </label>
    </div>
  );
}

/**
 * A whole-form failure — a wrong password, a server error.
 *
 * `aria-live="assertive"` because it appears in response to something the user
 * just did and is the reason the thing they tried did not happen.
 */
export function FormError({ children }: { children: ReactNode }) {
  if (!children) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex items-start gap-2 rounded-sm border border-status-priority-text/30 bg-red-50 px-3 py-2.5 text-body text-status-priority-text"
    >
      <AlertCircle aria-hidden="true" className="mt-0.5 size-[18px] shrink-0" />
      <span>{children}</span>
    </div>
  );
}
